import { Controller } from "@hotwired/stimulus"

// Controller for the Add Document form
// Handles file/URL mode switching, validation, and form submission
export default class extends Controller {
  static targets = [
    "form", "primaryFile", "companionPdfSection", "companionPdf",
    "companionOfficeSection", "companionOffice",
    "submitButton", "status", "folderPath", "modeInput",
    "fileTab", "urlTab", "fileSection", "urlSection", "urlInput",
    "transcriptSection", "transcriptInput"
  ]
  static values = {
    createUrl: String
  }

  connect() {
    // Check initial file state in case of browser back/forward
    if (this.hasPrimaryFileTarget && this.primaryFileTarget.files.length > 0) {
      this.fileSelected()
    }
    // URL replace mode starts on the URL tab — offer the transcript field.
    if (this.hasModeInputTarget && this.modeInputTarget.value === "url") {
      this.showTranscript()
    }
  }

  // === MODE SWITCHING ===

  showFileMode() {
    this.modeInputTarget.value = "file"

    // Update tab styles
    this.fileTabTarget.classList.add("border-blue-500", "text-blue-600")
    this.fileTabTarget.classList.remove("border-transparent", "text-gray-500")
    this.urlTabTarget.classList.remove("border-blue-500", "text-blue-600")
    this.urlTabTarget.classList.add("border-transparent", "text-gray-500")

    // Show/hide sections
    this.fileSectionTarget.classList.remove("hidden")
    this.urlSectionTarget.classList.add("hidden")

    // Clear URL input
    if (this.hasUrlInputTarget) {
      this.urlInputTarget.value = ""
    }

    // Transcript is re-shown by fileSelected() only for audio/video files.
    this.hideTranscript()

    this.clearStatus()
  }

  showUrlMode() {
    this.modeInputTarget.value = "url"

    // Update tab styles
    this.urlTabTarget.classList.add("border-blue-500", "text-blue-600")
    this.urlTabTarget.classList.remove("border-transparent", "text-gray-500")
    this.fileTabTarget.classList.remove("border-blue-500", "text-blue-600")
    this.fileTabTarget.classList.add("border-transparent", "text-gray-500")

    // Show/hide sections
    this.urlSectionTarget.classList.remove("hidden")
    this.fileSectionTarget.classList.add("hidden")

    // Clear file inputs
    if (this.hasPrimaryFileTarget) {
      this.primaryFileTarget.value = ""
    }
    this.hideCompanionPdf()
    this.hideCompanionOffice()

    // A video URL (e.g. YouTube) can be indexed by pasting a transcript.
    this.showTranscript()

    this.clearStatus()
  }

  // === FILE MODE HANDLING ===

  // Called when primary file is selected
  fileSelected() {
    const file = this.primaryFileTarget.files[0]
    if (!file) {
      this.hideCompanionPdf()
      this.hideTranscript()
      return
    }

    const ext = file.name.split('.').pop().toLowerCase()

    // DOCX/PPTX: offer an optional companion PDF (auto-generated if omitted).
    // PDF: suggest the optional Word/PowerPoint original for better indexing.
    if (ext === 'docx' || ext === 'pptx') {
      this.showCompanionPdf()
      this.hideCompanionOffice()
    } else if (ext === 'pdf') {
      this.showCompanionOffice()
      this.hideCompanionPdf()
    } else {
      this.hideCompanionPdf()
      this.hideCompanionOffice()
    }

    // Audio/video: offer an optional transcript to skip Whisper transcription.
    if (['mp3', 'mp4', 'm4v'].includes(ext)) {
      this.showTranscript()
    } else {
      this.hideTranscript()
    }
  }

  showCompanionPdf() {
    if (this.hasCompanionPdfSectionTarget) {
      this.companionPdfSectionTarget.classList.remove('hidden')
      this.companionPdfTarget.required = false
    }
  }

  hideCompanionPdf() {
    if (this.hasCompanionPdfSectionTarget) {
      this.companionPdfSectionTarget.classList.add('hidden')
      this.companionPdfTarget.required = false
      this.companionPdfTarget.value = ''
    }
  }

  showCompanionOffice() {
    if (this.hasCompanionOfficeSectionTarget) {
      this.companionOfficeSectionTarget.classList.remove('hidden')
    }
  }

  hideCompanionOffice() {
    if (this.hasCompanionOfficeSectionTarget) {
      this.companionOfficeSectionTarget.classList.add('hidden')
      this.companionOfficeTarget.value = ''
    }
  }

  showTranscript() {
    if (this.hasTranscriptSectionTarget) {
      this.transcriptSectionTarget.classList.remove('hidden')
    }
  }

  hideTranscript() {
    if (this.hasTranscriptSectionTarget) {
      this.transcriptSectionTarget.classList.add('hidden')
      if (this.hasTranscriptInputTarget) this.transcriptInputTarget.value = ''
    }
  }

  // === URL MODE HANDLING ===

  isYouTubeUrl(url) {
    const youtubePatterns = [
      /youtube\.com\/watch/i,
      /youtube\.com\/embed/i,
      /youtu\.be\//i,
      /youtube\.com\/v\//i
    ]
    return youtubePatterns.some(pattern => pattern.test(url))
  }

  // === FORM SUBMISSION ===

  async submit(event) {
    event.preventDefault()

    const mode = this.modeInputTarget.value

    if (mode === "file") {
      await this.submitFile()
    } else {
      await this.submitUrl()
    }
  }

  async submitFile() {
    // Validate primary file
    if (!this.primaryFileTarget.files.length) {
      this.showStatus("Please select a file to upload", "error")
      return
    }

    // Companion PDF is optional for DOCX/PPTX (auto-generated if omitted);
    // the companion Office file is optional for PDF. Nothing to block on.
    await this.doSubmit()
  }

  async submitUrl() {
    const url = this.urlInputTarget.value.trim()

    // Validate URL presence
    if (!url) {
      this.showStatus("Please enter a URL", "error")
      return
    }

    // Validate URL format
    try {
      new URL(url)
    } catch {
      this.showStatus("Please enter a valid URL", "error")
      return
    }

    // A YouTube link can be indexed only when a transcript is pasted; otherwise
    // guide the user to upload the video (or a short extract) as an MP4 file.
    if (this.isYouTubeUrl(url)) {
      const hasTranscript = this.hasTranscriptInputTarget && this.transcriptInputTarget.value.trim().length > 0
      if (!hasTranscript) {
        this.showStatus("A YouTube link can only be indexed if you paste its transcript below. Otherwise download the video (or a short extract) and upload it as an MP4 file.", "warning")
        return
      }
    }

    await this.doSubmit()
  }

  async doSubmit() {
    // Disable submit button and show progress
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.textContent = "Adding..."
    this.showStatus("Processing document...", "info")

    // Build form data
    const formData = new FormData(this.formTarget)

    try {
      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      const data = await response.json()

      if (!response.ok) {
        this.showStatus(data.error || 'Failed to add document', "error")
        this.resetForm()
        return
      }

      // Success!
      this.showStatus(`Document added: ${data.chunks_added} chunks indexed`, "success")

      // Close modal after short delay and reload page
      setTimeout(() => {
        this.closeModal()
        window.location.reload()
      }, 1500)

    } catch (error) {
      this.showStatus(`Error: ${error.message}`, "error")
      this.resetForm()
    }
  }

  // === UI HELPERS ===

  showStatus(message, type) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
      const colors = {
        info: 'text-blue-600',
        success: 'text-green-600',
        error: 'text-red-600',
        warning: 'text-yellow-600'
      }
      this.statusTarget.className = `text-sm font-medium ${colors[type] || 'text-gray-600'}`
    }
  }

  clearStatus() {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = ""
      this.statusTarget.className = "text-sm text-gray-600"
    }
  }

  resetForm() {
    this.submitButtonTarget.disabled = false
    this.submitButtonTarget.textContent = "Add Document"
  }

  closeModal() {
    const modalDiv = document.getElementById('modaldiv')
    if (modalDiv && modalDiv.parentElement) {
      modalDiv.parentElement.removeAttribute("src")
      modalDiv.remove()
    }
  }
}
